/**
 * A rig's crew templates (GET /well-servicing/rigs/:id → crews), on the rig's
 * own page.
 *
 * A crew is a named tour (day / night / …) on one rig, and its members are the
 * hands who usually work it, each in a position (rig manager, driller,
 * derrickhand, floorhand, …), ordered by `sort_order` then name. WS-1 stores
 * the template only; a ticket's crew is copied from it later (WS-2).
 *
 * WS_CREW_WRITE (admin, office_staff, supervisor, dispatcher) adds, edits and
 * removes crews and members; every other role reads. Each refusal reads as a
 * sentence in the dialog, never as a green toast: 409 duplicate_crew_name,
 * 409 already_on_crew, and a 400 for a terminated hand or a customer login.
 * The person picker leaves out anyone already on that crew.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useAddWsCrewMember,
  useCreateWsCrew,
  useDeleteWsCrew,
  useRemoveWsCrewMember,
  useUpdateWsCrew,
  useUpdateWsCrewMember,
  useWsUsers,
} from '../../hooks/useWellServicing';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, catalogLabel, wsRefusal } from '../../lib/well-servicing';
import type { WsCatalog, WsCrew, WsCrewMember, WsRigDetail } from '../../types/well-servicing';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsDialog, WsEmpty, WsField, WsFormError, WsPanel } from './WsBits';

type Dialog =
  | { kind: 'crew'; crew: WsCrew | null }
  | { kind: 'deleteCrew'; crew: WsCrew }
  | { kind: 'member'; crew: WsCrew; member: WsCrewMember | null }
  | { kind: 'removeMember'; crew: WsCrew; member: WsCrewMember }
  | null;

const SMALL_BTN =
  'inline-flex items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-2.5 py-1.5 text-xs font-semibold text-fieldiq-text hover:text-white disabled:opacity-60';

function CrewDialog({ rig, crew, catalog, onClose }: { rig: WsRigDetail; crew: WsCrew | null; catalog: WsCatalog | null; onClose: () => void }) {
  const create = useCreateWsCrew();
  const update = useUpdateWsCrew();
  const [name, setName] = useState(crew?.name ?? '');
  const [tour, setTour] = useState(crew?.tour ?? '');
  const [notes, setNotes] = useState(crew?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  const save = () => {
    setError(null);
    if (!name.trim()) return setError('Give the crew a name, e.g. A crew.');
    if (!tour) return setError('Pick the tour this crew works.');
    const done = { onSuccess: () => { toast.success(crew ? 'Crew saved' : 'Crew added'); onClose(); }, onError: (err: unknown) => setError(wsRefusal(err, 'The crew could not be saved.')) };
    if (!crew) return create.mutate({ rigId: rig.id, body: { name: name.trim(), tour, notes: blankToNull(notes) } }, done);
    const body: Record<string, unknown> = {};
    if (name.trim() !== crew.name) body.name = name.trim();
    if (tour !== crew.tour) body.tour = tour;
    if (blankToNull(notes) !== (crew.notes ?? null)) body.notes = blankToNull(notes);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    update.mutate({ id: crew.id, body }, done);
  };

  return (
    <WsDialog
      open
      title={crew ? `Edit ${crew.name}` : `Add a crew to ${rig.rigNumber}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WS_PRIMARY} onClick={save} disabled={pending}>
            {pending ? 'Saving…' : crew ? 'Save crew' : 'Add crew'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="ws-crew-dialog">
        <WsFormError message={error} />
        <WsField label="Crew name">
          <input className={WS_INPUT} maxLength={100} value={name} onChange={(e) => setName(e.target.value)} aria-label="Crew name" />
        </WsField>
        <WsField label="Tour">
          <select className={WS_INPUT} value={tour} onChange={(e) => setTour(e.target.value)} aria-label="Tour">
            <option value="">Select a tour…</option>
            {(catalog?.crewTours ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Notes">
          <textarea className={`${WS_INPUT} h-20 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Crew notes" />
        </WsField>
      </div>
    </WsDialog>
  );
}

function MemberDialog({
  crew,
  member,
  catalog,
  onClose,
}: {
  crew: WsCrew;
  member: WsCrewMember | null;
  catalog: WsCatalog | null;
  onClose: () => void;
}) {
  const users = useWsUsers(!member);
  const add = useAddWsCrewMember();
  const update = useUpdateWsCrewMember();
  const [userId, setUserId] = useState('');
  const [position, setPosition] = useState(member?.position ?? '');
  const [order, setOrder] = useState(member ? String(member.sortOrder) : '');
  const [error, setError] = useState<string | null>(null);
  const pending = add.isPending || update.isPending;
  const onCrew = new Set(crew.members.map((m) => m.userId));
  const candidates = (users.data ?? []).filter((u) => !onCrew.has(u.id));
  const usersFailure = queryLoadFailure(users);
  // A new hand goes to the bottom of the crew's order.
  const nextOrder = crew.members.reduce((n, m) => Math.max(n, m.sortOrder ?? 0), 0) + 10;

  const save = () => {
    setError(null);
    if (!member && !userId) return setError('Pick the person to add.');
    if (!position) return setError('Pick their position on the crew.');
    const done = { onSuccess: () => { toast.success(member ? 'Crew member saved' : 'Added to the crew'); onClose(); }, onError: (err: unknown) => setError(wsRefusal(err, 'The crew member could not be saved.')) };
    if (!member) return add.mutate({ crewId: crew.id, body: { user_id: userId, position, sort_order: Math.min(nextOrder, 100000) } }, done);
    const body: Record<string, unknown> = {};
    if (position !== member.position) body.position = position;
    if (order.trim() !== String(member.sortOrder)) {
      const n = Number(order.trim());
      if (!/^\d+$/.test(order.trim()) || n > 100000) return setError('Order must be a whole number from 0 to 100000.');
      body.sort_order = n;
    }
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    update.mutate({ id: member.id, body }, done);
  };

  return (
    <WsDialog
      open
      title={member ? `Edit ${member.userName ?? 'crew member'}` : `Add to ${crew.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WS_PRIMARY} onClick={save} disabled={pending || (!member && !!usersFailure)}>
            {pending ? 'Saving…' : member ? 'Save' : 'Add to crew'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="ws-member-dialog">
        <WsFormError message={error} />
        {!member &&
          (users.isLoading ? (
            <p className="text-sm text-fieldiq-muted">Loading people…</p>
          ) : usersFailure ? (
            <LoadFailureNotice error={queryFailureError(users)} kind={usersFailure} subject="your people" />
          ) : (
            <WsField label="Person" hint={candidates.length === 0 ? 'Everyone is already on this crew.' : undefined}>
              <select className={WS_INPUT} value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Person">
                <option value="">Select a person…</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </WsField>
          ))}
        <WsField label="Position">
          <select className={WS_INPUT} value={position} onChange={(e) => setPosition(e.target.value)} aria-label="Position">
            <option value="">Select a position…</option>
            {(catalog?.crewPositions ?? []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </WsField>
        {member && (
          <WsField label="Order" hint="Lower comes first on the crew list.">
            <input className={WS_INPUT} inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} aria-label="Order" />
          </WsField>
        )}
      </div>
    </WsDialog>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  keepLabel,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  keepLabel: string;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <WsDialog
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WS_SECONDARY} onClick={onClose} disabled={pending}>
            {keepLabel}
          </button>
          <button type="button" className={WS_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <WsFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </WsDialog>
  );
}

export function RigCrews({ rig, catalog, canWrite }: { rig: WsRigDetail; catalog: WsCatalog | null; canWrite: boolean }) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const delCrew = useDeleteWsCrew();
  const removeMember = useRemoveWsCrewMember();
  const crews = rig.crews ?? [];
  const close = () => {
    setDialog(null);
    setConfirmError(null);
  };

  return (
    <WsPanel
      eyebrow="Crew templates"
      title="Crews"
      testId="ws-rig-crews"
      actions={
        canWrite ? (
          <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'crew', crew: null })}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add crew
          </button>
        ) : undefined
      }
    >
      {crews.length === 0 ? (
        <WsEmpty>This rig has no crews yet.</WsEmpty>
      ) : (
        <div className="divide-y divide-fieldiq-border">
          {crews.map((crew) => (
            <div key={crew.id} className="space-y-2 px-4 py-4" data-testid="ws-crew">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-extrabold text-white">{crew.name}</p>
                  <p className="text-xs text-fieldiq-muted">
                    {crew.tourLabel ?? catalogLabel(catalog?.crewTours, crew.tour)} · {crew.members.length} hand{crew.members.length === 1 ? '' : 's'}
                  </p>
                  {crew.notes && <p className="mt-1 text-sm text-fieldiq-text">{crew.notes}</p>}
                </div>
                {canWrite && (
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'member', crew, member: null })}>
                      <UserPlus className="size-3.5" aria-hidden="true" />
                      Add hand
                    </button>
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'crew', crew })} aria-label={`Edit ${crew.name}`}>
                      <Pencil className="size-3.5" aria-hidden="true" />
                      Edit
                    </button>
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'deleteCrew', crew })} aria-label={`Delete ${crew.name}`}>
                      <Trash2 className="size-3.5" aria-hidden="true" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              {crew.members.length === 0 ? (
                <p className="text-sm text-fieldiq-muted">Nobody is on this crew yet.</p>
              ) : (
                <ul className="divide-y divide-fieldiq-border/60 rounded-fi-control border border-fieldiq-border">
                  {crew.members.map((m) => (
                    <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid="ws-crew-member">
                      <div className="min-w-0">
                        <span className="text-sm font-semibold text-white">{m.userName || '—'}</span>
                        <span className="ml-2 text-xs text-fieldiq-muted">{m.positionLabel ?? catalogLabel(catalog?.crewPositions, m.position)}</span>
                        {m.employmentStatus === 'terminated' && (
                          <span className="ml-2 text-xs font-semibold text-fi-signal-red">No longer employed</span>
                        )}
                      </div>
                      {canWrite && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className={SMALL_BTN}
                            onClick={() => setDialog({ kind: 'member', crew, member: m })}
                            aria-label={`Edit ${m.userName ?? 'crew member'}`}
                          >
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={SMALL_BTN}
                            onClick={() => setDialog({ kind: 'removeMember', crew, member: m })}
                            aria-label={`Remove ${m.userName ?? 'crew member'}`}
                          >
                            <UserMinus className="size-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog?.kind === 'crew' && <CrewDialog rig={rig} crew={dialog.crew} catalog={catalog} onClose={close} />}
      {dialog?.kind === 'member' && <MemberDialog crew={dialog.crew} member={dialog.member} catalog={catalog} onClose={close} />}
      {dialog?.kind === 'deleteCrew' && (
        <ConfirmDialog
          title={`Delete ${dialog.crew.name}`}
          body={`Deletes the crew and takes its ${dialog.crew.members.length} hand${dialog.crew.members.length === 1 ? '' : 's'} off it. Nobody is removed from the company.`}
          confirmLabel="Delete crew"
          keepLabel="Keep crew"
          pending={delCrew.isPending}
          error={confirmError}
          onClose={close}
          onConfirm={() =>
            delCrew.mutate(dialog.crew.id, {
              onSuccess: () => {
                toast.success('Crew deleted');
                close();
              },
              onError: (err) => setConfirmError(wsRefusal(err, 'The crew could not be deleted.')),
            })
          }
        />
      )}
      {dialog?.kind === 'removeMember' && (
        <ConfirmDialog
          title={`Remove ${dialog.member.userName ?? 'crew member'}`}
          body={`Takes ${dialog.member.userName ?? 'this person'} off ${dialog.crew.name}. They stay in the company.`}
          confirmLabel="Remove"
          keepLabel="Keep"
          pending={removeMember.isPending}
          error={confirmError}
          onClose={close}
          onConfirm={() =>
            removeMember.mutate(dialog.member.id, {
              onSuccess: () => {
                toast.success('Removed from the crew');
                close();
              },
              onError: (err) => setConfirmError(wsRefusal(err, 'The crew member could not be removed.')),
            })
          }
        />
      )}
    </WsPanel>
  );
}
